import invariant from 'invariant'
import conformsTo from 'lodash/fp/conformsTo'
import isFunction from 'lodash/fp/isFunction'
import isObject from 'lodash/fp/isObject'
import isString from 'lodash/fp/isString'
import isEmpty from 'lodash/fp/isEmpty'
import { DAEMON, ONCE_TILL_UNMOUNT } from './constants'

/** @type {Function} function to test store shape */
const storeShape = conformsTo({
  dispatch: isFunction,
  subscribe: isFunction,
  getState: isFunction,
  replaceReducer: isFunction,
  runSaga: isFunction,
  injectedReducers: isObject,
  injectedSagas: isObject,
  history: isObject
})

/**
 * check if provided store is valid redux store
 *
 * @param {Object} store - redux store to check
 * @param {String} [source='(state/utils): injectors'] - source string path to help error
 * @throws {InvariantError} if is not valid store
 */
export const checkStore = (store, source = '(state/utils) injectors') => invariant(
  storeShape(store),
  `${source}: expect a valid redux store`
)

/**
 * check if provided key is valid
 *
 * @param {String} key - key to check
 * @param {String} [source='(state/utils): injectors'] - source string path to help error
 * @throws {InvariantError} if key is not string or is empty
 */
export const checkKey = (key, source = '(state/utils) injectors') => invariant(
  isString(key) && !isEmpty(key),
  `${source}: expect \`key\` to be a non empty string`
)

/**
 * check if provided reducer is function
 *
 * @param {Function} reducer - reducer to check
 * @param {String} [source='(state/utils): injectors'] - source string path to help error
 * @throws {InvariantError} if key is not string or is empty
 */
export const checkReducer = (key, source = '(state/utils) injectors') => invariant(
  isFunction(key),
  `${source}: expect \`reducer\` to be a reducer function`
)

/** @type {[String]} allowed saga modes */
const allowedSagaModes = [DAEMON, ONCE_TILL_UNMOUNT]

/** @type {Function} function to test saga descriptor shape */
const sagaDescriptorShape = conformsTo({
  saga: isFunction,
  mode: mode => allowedSagaModes.includes(mode)
})

/**
 * check if provided saga descriptor is valid
 *
 * @param {Object} descriptor - saga descriptor object
 * @param {String} [source='(state/utils): injectors'] - source string path to help error
 * @throws {InvariantError} if descriptor is invalid
 */
export const checkSagaDescriptor = (descriptor, source = '(state/utils) injectors') => invariant(
  sagaDescriptorShape(descriptor),
  `${source}: expect \`sagaDescriptor\` to be a valid descriptor shape`
)
